import { Label } from '../ui/label';
import StyledTooltipContainer from '../styled/StyledTooltipContainer';
import CloseIcon from '@/assets/icons/close.svg?react';
import clsx from 'clsx';
import { CircuitBenefitType } from '@/types/circuits/CircuitBenefitType';
import {
  CircuitStepDaytime,
  CircuitStepDayTimeIndexes,
} from '@/types/circuits/CircuitStepDaytime';
import { HotelPensionType } from '@/types/benefits/hotel/HotelResponse';
import { useTranslation } from '@/i18n';

type ValueOf<T> = T[keyof T];

const BENEFIT_DISPLAY_MAPPER = {
  n: 'name',
  c: 'shortDescription',
  l: 'longDescription',
} as const;

const PENSION_DISPLAY_MAPPER: Record<HotelPensionType, string> = {
  SingleRoom: 'RO',
  Breakfast: 'BB',
  SemiPension: 'HB',
  FullPension: 'FB',
  AllInclusive: 'AI',
};

interface CircuitEditorialBenefitElementProps {
  type: CircuitBenefitType;
  title: string;
  onRemove?: () => void;
  onLabelClick?: () => void;
  isStandard?: boolean;
  isViewMode?: boolean;
  isAssociated?: boolean;
  hideRemove?: boolean;
  disableClick?: boolean;
  showEditorialFlags?: boolean;
  benefitProperties?: {
    indexStepDay: number;
    stepNbDays: number;
    benefitDisplays?: ValueOf<typeof BENEFIT_DISPLAY_MAPPER>[];
    time?: CircuitStepDaytime;
    pensionType?: HotelPensionType;
  };
}

const CircuitEditorialBenefitElement = ({
  type,
  onRemove,
  title,
  onLabelClick,
  isViewMode = false,
  isStandard = false,
  isAssociated = false,
  hideRemove = false,
  disableClick = false,
  showEditorialFlags = true,
  benefitProperties,
}: CircuitEditorialBenefitElementProps) => {
  const { t } = useTranslation();
  const content = (
    <div
      className={clsx(
        'flex items-center justify-between px-3.5 py-2 rounded-lg w-full',
        {
          border: !isStandard,
          'border-2': isStandard,
          'bg-[#FFEBEB] border-warning-500': type === 'Transport',
          'bg-[#F3EBFF] border-brand-500': type === 'Activités',
          'bg-[#EBF6FF] border-sky-700': type === 'Repas',
          'bg-[#EBFFEF] border-success-600': type === 'Hébergements',
        },
      )}
      style={
        isAssociated
          ? {
              backgroundImage:
                'repeating-linear-gradient(-45deg, transparent, transparent 4px, rgba(0,0,0,0.06) 4px, rgba(0,0,0,0.06) 8px)',
            }
          : undefined
      }
    >
      <div className="flex flex-col w-full">
        <div
          className={clsx('w-full', {
            'hover:cursor-pointer': !disableClick,
          })}
          onClick={disableClick ? undefined : onLabelClick}
        >
          <Label className={clsx({ 'hover:cursor-pointer': !disableClick })}>
            {title}
          </Label>
        </div>
        {benefitProperties && (
          <div className="flex justify-end gap-1.5 w-full">
            <p className="text-sm text-purple-800">
              {benefitProperties.indexStepDay + 1}/
              {benefitProperties.stepNbDays}
            </p>
            <p className="text-sm text-success-700">
              {showEditorialFlags
                ? Object.entries(BENEFIT_DISPLAY_MAPPER)
                    .map(([k, v]) =>
                      benefitProperties.benefitDisplays?.includes(v) ? k : '-',
                    )
                    .join('/')
                : '-/-/-'}
            </p>
            {benefitProperties.time && (
              <p className="text-sm text-blue-800">
                {CircuitStepDayTimeIndexes[benefitProperties.time] + 1}/
                {CircuitStepDayTimeIndexes[CircuitStepDaytime.Night] + 1}
              </p>
            )}
            {type === 'Hébergements' && benefitProperties.pensionType && (
              <p className="text-sm text-black">
                {PENSION_DISPLAY_MAPPER[benefitProperties.pensionType]}
              </p>
            )}
          </div>
        )}
      </div>
      {!isViewMode && !hideRemove && onRemove && (
        <StyledTooltipContainer
          tooltipMessage={t('circuits.editorial.tooltip.remove')}
        >
          <CloseIcon className="size-5 fill-black" onClick={onRemove} />
        </StyledTooltipContainer>
      )}
    </div>
  );

  if (isAssociated) {
    return (
      <StyledTooltipContainer
        tooltipMessage={t('circuits.editorial.tooltip.associatedBenefit')}
      >
        {content}
      </StyledTooltipContainer>
    );
  }

  return content;
};

export default CircuitEditorialBenefitElement;
